import React, { useCallback, useEffect } from 'react';
import { useForm } from '@nerdgeschoss/react-use-form-library';

import { t } from '../../../../../locales/translate';
import { Card } from '../../../../../components/Card/Card';
import { Tag } from '../../../../../components/Tag/Tag';
import { Modal } from '../../../../../components/Modal/Modal';
import { Icon } from '../../../../../components/Icon/Icon';
import { graphql } from '../../../../../generated';
import { useMutationHandler, useQueryHandler } from '../../../../../model/graphql';
import { FormTextField } from '../../../../../components/Form/TextField/FormTextField';
import { Stack } from '../../../../../components/Stack/Stack';
import { Label } from '../../../../../components/Form/Label/Label';
import { FormForm } from '../../../../../components/Form/FormForm';
import { CodeEditor } from '../../../../../components/code_editor/code_editor';
import { FormSwitcher } from '../../../../../components/Form/form_switcher/form_switcher';
import { exists } from '../../../../../util/util';

export const EVENT_LOCAL_TICKET_TEMPLATE_SIMPLE = graphql(`
  query EventLocalTicketTemplateSimple($eventId: ID!) {
    event(id: $eventId) {
      id
      localTicketEmailTemplate {
        id
        enabled
        replyToEmail
        templateHtml
      }
    }
  }
`);

export const UPDATE_LOCAL_TICKET_EMAIL_TEMPLATE = graphql(`
  mutation UpdateLocalTicketEmailTemplate(
    $eventId: ID!
    $enabled: Boolean!
    $replyToEmail: String
    $templateHtml: String
  ) {
    updateLocalTicketEmailTemplate(
      input: { eventId: $eventId, enabled: $enabled, replyToEmail: $replyToEmail, templateHtml: $templateHtml }
    ) {
      localTicketEmailTemplate {
        id
        enabled
        replyToEmail
        templateHtml
      }
    }
  }
`);

interface Props {
  eventId: string;
}

const locales = t.model.ticketEmailTemplate;

const shortCodes = [
  { id: '*|FNAME|*', label: locales.modal.firstName },
  { id: '*|LNAME|*', label: locales.modal.lastName },
  { id: '*|EMAIL|*', label: locales.modal.email },
  { id: '*|PHONE|*', label: locales.modal.phone },
  { id: '*|TICKETS|*', label: locales.modal.ticketCount },
  { id: '*|TEXT|*', label: locales.modal.emailText },
];

export function LocalTicketEmailTemplate({ eventId }: Props): JSX.Element {
  const [editing, setEditing] = React.useState(false);
  const { data, loading, refetch } = useQueryHandler(EVENT_LOCAL_TICKET_TEMPLATE_SIMPLE, {
    variables: {
      eventId: eventId,
    },
  });
  const [updateLocalTicketEmailTemplate, { loading: emailUpdateLoading }] = useMutationHandler(
    UPDATE_LOCAL_TICKET_EMAIL_TEMPLATE
  );

  const handleOnSubmit = useCallback(
    async (formData) => {
      await updateLocalTicketEmailTemplate({
        variables: {
          eventId,
          replyToEmail: formData.model.emailAddress,
          templateHtml: formData.model.customEmailTemplate,
          enabled: formData.model.customEmailEnabled,
        },
      });
    },
    [eventId, updateLocalTicketEmailTemplate]
  );

  const { fields, model, onSubmit, valid, dirty, updateFields, submissionStatus } = useForm({
    model: {
      customEmailEnabled: false,
      emailAddress: '',
      customEmailTemplate: '',
    },
    onSubmit: handleOnSubmit,
    validations: {
      emailAddress: ['email'],
      customEmailEnabled: 'required',
    },
  });

  useEffect(() => {
    if (exists(data?.event?.localTicketEmailTemplate)) {
      const { enabled: customEmailEnabled, replyToEmail, templateHtml } = data.event.localTicketEmailTemplate;

      updateFields({
        customEmailEnabled,
        emailAddress: replyToEmail || undefined,
        customEmailTemplate: templateHtml || undefined,
      });
    }
  }, [data]);

  useEffect(() => {
    if (submissionStatus === 'submitted') {
      setEditing(false);
      refetch();
    }
  }, [submissionStatus]);

  const { enabled, templateHtml } = data?.event?.localTicketEmailTemplate || { enabled: false, templateHtml: null };

  const displayPreview = enabled && templateHtml;

  return (
    <>
      <Card
        loading={loading}
        title={
          <>
            {locales.cardTitle}
            <Tag text={model.customEmailEnabled ? locales.tags.custom : locales.tags.default} />
          </>
        }
        actions={<Icon name="edit" tooltip="Edit" onClick={() => setEditing(true)} />}
      >
        <Stack>
          <Label title={locales.emailLabel} />
          <p>
            <strong>{model.emailAddress || locales.noEmailSet}</strong>
          </p>
          {displayPreview && !editing && (
            <>
              <Label title={locales.previewLabel} />
              <iframe
                srcDoc={templateHtml}
                style={{
                  width: '100%',
                  height: 'min(80vh, 600px)',
                }}
              />
            </>
          )}
        </Stack>
      </Card>
      <Modal open={editing} size="large" title={locales.modal.title}>
        <FormForm
          onSubmit={onSubmit}
          onClose={() => {
            setEditing(false);
          }}
          canSubmit={valid && dirty}
          loading={emailUpdateLoading}
        >
          <Stack>
            <Label title={locales.modal.emailLabel} />
            <FormTextField
              {...fields.emailAddress}
              label={locales.modal.fieldLabel}
              placeholder="email@krasserstoff.de"
            />

            <Label title={locales.modal.switcher.label} />
            <FormSwitcher
              labels={[locales.modal.switcher.yes, locales.modal.switcher.no]}
              onChange={(val) => fields.customEmailEnabled.onChange(val || false)}
              value={fields.customEmailEnabled.value}
            />

            {model.customEmailEnabled && (
              <Stack>
                <Stack size="xs">
                  <h3>{t.model.ticketEmailTemplate.modal.templateVariables}</h3>
                  <div>
                    {shortCodes.map((code) => (
                      <p key={code.id}>
                        <strong>{code.id} : </strong>
                        {code.label}
                      </p>
                    ))}
                  </div>
                </Stack>
                <CodeEditor
                  autofocus
                  value={fields.customEmailTemplate.value}
                  onChange={(e) => fields.customEmailTemplate.onChange(e.target.value)}
                />
              </Stack>
            )}
          </Stack>
        </FormForm>
      </Modal>
    </>
  );
}
